import React, { useState, useEffect } from 'react';
import { useDeepCompareEffect, useIsVertTablet } from 'utils/hooks';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { Helmet } from 'react-helmet';
import { dataLayerPush } from 'utils/tracking';
import { Button, H2, Span, Section } from 'components/Ui';
import { Grid } from '@material-ui/core';
import Container from 'components/Ui/Container';
import Loader from 'components/Loader';
import withContentful from 'containers/Landing/withContenfull';
import { pages } from 'containers/Landing/constants';
import ContentfulFields from 'components/ContentfulFields';
import { ProductListItem } from 'components/ContentfulFields/ProductCard';
import PriceSummary from 'containers/PrimoCart/PriceSummary';
import { HiddenContent } from 'components/Ui/ScreenReader';
import { selectCartData } from 'containers/Cart/selectors';
import { getToUrl, translateData } from 'utils/translation';
import { connect, useSelector } from 'react-redux';
import {
  selectAcquisitionVariables,
  selectSsrReqRoute,
} from 'containers/Landing/selectors';
import Header from './Header';
import styles from './styles.scss';

const WaterCart = ({
  site: {
    components: { page },
    loading,
  },
  cart: { id: cartId, items },
  cartSummary,
  cartLoading,
  cartInitiated,
  cartProductsLoading,
  cartSummaryLoading,
  removeItem,
  loadCartProducts,
  changeItemQuantity,
}) => {
  const [isCostco, setIsCostco] = useState(false);
  const [quantityContent, updateQuantityContent] = useState(null);

  const isVertTablet = useIsVertTablet();
  const md1 = isVertTablet ? 8 : 9;
  const md2 = isVertTablet ? 4 : 3;

  useDeepCompareEffect(() => {
    if (!isEmpty(items)) {
      loadCartProducts(items);
    }
  }, [items]);

  const cartData = useSelector(selectCartData());

  const handleRemove = (item) => {
    updateQuantityContent(`${item.name} removed from cart.`);
    removeItem({ cartId, itemId: item.id });
    // mmm rrr handleRemove
    dataLayerPush(
      'Acquisition',
      {
        event: 'removeFromCart',
        ecommerce: {
          remove: {
            products: cartData.items.map((data) => ({
              brand: data.brand || '',
              category: data.category,
              id: data.itemNumber,
              name: data.name,
              price: `${data.price}`,
              quantity: data.quantity,
              variant: data.size,
            })),
          },
        },
      },
      'dlA10',
      'PUSH_REMOVE_FROM_CART_DATA',
    );
  };

  const handleQuantityChange = (item, quantity) => {
    changeItemQuantity({ cartId, item, quantity });
  };

  useEffect(() => {
    setIsCostco(window.isCostcoWater);
  }, []);

  // Todo: Implement VTable mode

  const href = isCostco
    ? '/quick-shop/select-water'
    : getToUrl('/products/', 'U67');

  return (
    <article className={styles.cart}>
      <Helmet title={translateData(`Cart`)} />
      <Header cartSummary={cartSummary} />
      <Loader
        isLoading={
          !cartInitiated ||
          loading ||
          cartLoading ||
          cartProductsLoading ||
          cartSummaryLoading
        }
        local
      >
        <Container value="Water Cart">
          {!cartLoading && isEmpty(items) ? (
            <div className={styles.empty}>
              <H2>Oh no!</H2>
              <p>
                <Span>Your cart is empty…</Span>
                <br />
                <Span>Go to the Our Products page to begin shopping.</Span>
              </p>
              <Button link href={href} text="Our Products Page" />
            </div>
          ) : (
            <Grid container spacing={3} className={styles.container}>
              <Grid item md={md1} xs={12}>
                <Section>
                  <HiddenContent aria-live="polite" aria-atomic="true">
                    {quantityContent && <span>{quantityContent}</span>}
                  </HiddenContent>
                  {items.map((item) => (
                    <ProductListItem
                      onDelete={handleRemove}
                      onQuantityChange={handleQuantityChange}
                      product={item}
                      className={styles.product}
                      removable
                      key={item.itemNumber}
                      usePropsCountValue
                      checkoutView
                      cartView
                    />
                  ))}
                </Section>
              </Grid>
              {cartSummary && (
                <Grid item md={md2} xs={12}>
                  <aside>
                    <PriceSummary
                      {...cartSummary}
                      cartItems={items}
                      isShowCheckoutButton
                    />
                  </aside>
                </Grid>
              )}
            </Grid>
          )}
          {!isCostco &&
            get(page, 'contentModules', []).map((banner) => (
              <ContentfulFields
                type={get(banner, 'sys.contentType.sys.id')}
                document={banner}
                key={banner.sys.id}
              />
            ))}
          <div className={styles.disclaimer} aria-hidden="true">
            <small>
              <sup className={styles.supScript}>1 </sup>
              <Span>
                Some areas subject to 4-week delivery based on service routes.
              </Span>
            </small>
          </div>
        </Container>
      </Loader>
    </article>
  );
};
const mapStateToProps = createStructuredSelector({
  acquisitionVariables: selectAcquisitionVariables(),
  ssrReqRoute: selectSsrReqRoute(),
});
WaterCart.propTypes = {
  site: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  cartSummary: PropTypes.object.isRequired,
  cartLoading: PropTypes.bool,
  cartInitiated: PropTypes.bool,
  removeItem: PropTypes.func.isRequired,
  loadCartProducts: PropTypes.func.isRequired,
  changeItemQuantity: PropTypes.func.isRequired,
  cartProductsLoading: PropTypes.bool,
  cartSummaryLoading: PropTypes.bool,
  acquisitionVariables: PropTypes.array,
  ssrReqRoute: PropTypes.object,
};

const CartPage = connect(mapStateToProps)(WaterCart);
export default withContentful(pages.WaterCart)(CartPage);
